import type { ReactElement } from "react";

import { translate } from "app/filters/i18nFilter";
import type { IFilterPanelBaseFilterModel } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filters";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import {
    FiltersChip
} from "app/pages/sales-intelligence/common-components/filters-panel";
import { FilterChipVariant } from "app/pages/sales-intelligence/common-components/filters-panel/types/commonEnums";
import formatChipsContent from "app/pages/sales-intelligence/modules/find-apps-filters/components/filters/AppDownloadEngagementFilter/utils/formatChipsContent";
import type { IAppsDownloadsAndEngagementFilter } from "app/pages/sales-intelligence/store/find-apps/types";
import CountryService from "app/services/CountryService";

import { StyledDownloadEngagementResultContainer } from "./styles";

type DownloadEngagementFilterResultProps<
    TFilter extends IFilterPanelBaseFilterModel<TValue>,
    TValue extends IAppsDownloadsAndEngagementFilter,
> = IFilterViewBaseProps<TFilter> & {
    hasDeviceFilter?: boolean;
};

const DownloadEngagementFilterResult = <
    TFilter extends IFilterPanelBaseFilterModel<TValue>,
    TValue extends IAppsDownloadsAndEngagementFilter,
>({
    filter,
    value,
    onFilterUpdate,
    hasDeviceFilter = false,
}: DownloadEngagementFilterResultProps<TFilter, TValue>): ReactElement | null => {
    const initValue = filter.getInitialDtoValue();
    const country = CountryService.getCountryById(value?.country || initValue?.country);

    const handleClose = (filterKey: keyof TValue) => {
        const mappedValue = { ...value } as TValue;
        delete mappedValue[filterKey];
        onFilterUpdate(mappedValue);
    };

    return (
        <StyledDownloadEngagementResultContainer>
            <FiltersChip
                id={country.id}
                text={country.text}
                hideActionButton
                variant={FilterChipVariant.Country}
            />

            {hasDeviceFilter && value?.device ? (
                <FiltersChip
                    text={translate(`si.find_apps.filters.ios-download-and-engagement.device.${value.device}`)}
                    onClose={() => handleClose("device" as keyof TValue)}
                />
            ) : null}

            {value?.monthlyDownloads ? (
                <FiltersChip
                    text={translate("si.find_apps.filters.download-and-engagement.monthly-downloads.range.chip", {
                        value: formatChipsContent(value.monthlyDownloads),
                    })}
                    onClose={() => handleClose("monthlyDownloads" as keyof TValue)}
                />
            ) : null}

            {value?.monthlyActiveUsers ? (
                <FiltersChip
                    text={translate("si.find_apps.filters.download-and-engagement.mau.range.chip", {
                        value: formatChipsContent(value.monthlyActiveUsers),
                    })}
                    onClose={() => handleClose("monthlyActiveUsers" as keyof TValue)}
                />
            ) : null}
        </StyledDownloadEngagementResultContainer>
    );
};

export default DownloadEngagementFilterResult;
